/// EditorCanvas: Canvas element connected to a WASM editor worker.
/// Uses useEditorWorker hook to manage OffscreenCanvas transfer, resize, and input forwarding.
/// Shows a loading overlay during worker initialization.
///
/// Panel-aware: uses panelId to check per-panel active tab instead of the
/// global activeTabId, ensuring that only the active tab in THIS panel
/// processes palette click-to-add events.

import { useRef, useEffect, useCallback, useState } from "react";
import { useEditorWorker } from "@/hooks/useEditorWorker";
import { useEditorStore, type PanelId } from "@/stores/editorStore";
import { getWorkerManager } from "@/lib/WorkerManager";
import { serializeMouseEvent, serializeWheelEvent } from "@lib/InputEvent.js";
import type { EditorType } from "@/types/editor";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditorCanvasProps {
  /** Unique tab ID for this editor instance. */
  tabId: string;
  /** Which editor type (bpmn or nodeflow). */
  editorType: EditorType;
  /** Panel identity for per-panel event filtering. */
  panelId: PanelId;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditorCanvas({ tabId, editorType, panelId }: EditorCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    isReady,
    error,
    initEditor,
    resize,
    forwardInput,
    addElement,
    zoom,
    fitToView,
    undo,
    redo,
    deleteSelected,
    copy,
    cut,
    paste,
    selectAll,
    loadDocument,
    saveDocument,
    importN8N,
    setProperty,
    autoLayout,
    addConnection,
    validate,
    startInlineEdit,
    commitInlineEdit,
    cancelInlineEdit,
    setFlowCondition,
    setDefaultFlow,
    setMultiInstance,
    attachBoundaryEvent,
    detachBoundaryEvent,
    toggleSubprocess,
    createPool,
    createLane,
    addSocket,
    removeSocket,
    groupSelected,
    dissolveGroup,
    createAndConnect,
    toggleCollapse,
    queryHoveredGroup,
    workerId,
  } = useEditorWorker({ editorType });

  // Cursor CSS based on active tool mode
  const toolMode = useEditorStore((s) => {
    const state = s.editorStates.get(tabId);
    return state?.toolMode ?? "select";
  });
  const cursorClass =
    toolMode === "addConnection"
      ? "cursor-crosshair"
      : toolMode === "pan"
        ? "cursor-grab"
        : "cursor-default";

  // Inline edit overlay state (Phase 15, Plan 09 -- gap closure)
  const [inlineEdit, setInlineEdit] = useState<{
    active: boolean;
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // Context menu state for node editor (Phase 26, Plan 02)
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
  } | null>(null);

  // Hovered group info from editorStore for tooltip display (Phase 26, Plan 02)
  const hoveredGroup = useEditorStore((s) => {
    const state = s.editorStates.get(tabId);
    return state?.hoveredGroup ?? null;
  });

  // Collapsed group IDs from editorStore for context menu label (Phase 26, Plan 02)
  const collapsedGroupIds = useEditorStore((s) => {
    const state = s.editorStates.get(tabId);
    return state?.collapsedGroupIds ?? null;
  });

  // Throttle ref for hover group queries (Phase 26, Plan 02)
  const hoverThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize the editor with OffscreenCanvas on mount.
  // The useEditorWorker hook handles StrictMode internally:
  // - createWorker() is idempotent (skips if worker exists)
  // - transferControlToOffscreen errors are caught gracefully
  useEffect(() => {
    if (canvasRef.current) {
      initEditor(canvasRef.current);
    }
  }, [initEditor]);

  // ResizeObserver for container sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          resize(Math.round(width), Math.round(height));
        }
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [resize]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      forwardInput(serializeMouseEvent(e.nativeEvent, "mousedown"));
    },
    [forwardInput],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      forwardInput(serializeMouseEvent(e.nativeEvent, "mousemove"));
      // Throttled hover query for collapsed group tooltip (Phase 26, Plan 02)
      if (editorType === "nodeflow" && !hoverThrottleRef.current) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          const dpr = window.devicePixelRatio || 1;
          const screenX = (e.clientX - rect.left) * dpr;
          const screenY = (e.clientY - rect.top) * dpr;
          hoverThrottleRef.current = setTimeout(() => {
            hoverThrottleRef.current = null;
            queryHoveredGroup(screenX, screenY);
          }, 100);
        }
      }
    },
    [forwardInput, editorType, queryHoveredGroup],
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      forwardInput(serializeMouseEvent(e.nativeEvent, "mouseup"));
    },
    [forwardInput],
  );

  // Wheel events must use native listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      forwardInput(serializeWheelEvent(e));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [forwardInput]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Only show context menu for node editor tabs with a selection
      if (editorType !== "nodeflow") return;
      const state = useEditorStore.getState();
      const editorState = state.editorStates.get(tabId);
      if (!editorState?.selection?.selectedIds?.length) return;
      setContextMenu({ visible: true, x: e.clientX, y: e.clientY });
    },
    [tabId, editorType],
  );

  // Close context menu on click anywhere, Escape, or scroll (Phase 26, Plan 02)
  useEffect(() => {
    if (!contextMenu?.visible) return;
    const closeMenu = () => setContextMenu(null);
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [contextMenu?.visible]);

  // Context menu: handle collapse/expand click (Phase 26, Plan 02)
  const handleCollapseMenuClick = useCallback(() => {
    const state = useEditorStore.getState();
    const editorState = state.editorStates.get(tabId);
    const selectedIds = editorState?.selection?.selectedIds ?? [];
    if (selectedIds.length > 0) {
      const groupId = parseInt(selectedIds[0], 10);
      if (!isNaN(groupId)) {
        toggleCollapse(groupId);
      }
    }
    setContextMenu(null);
  }, [tabId, toggleCollapse]);

  // Keyboard shortcuts for collapse/expand (Phase 26, Plan 02)
  // Ctrl+Shift+[ collapses, Ctrl+Shift+] expands
  useEffect(() => {
    if (editorType !== "nodeflow") return;
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      if (e.key !== "[" && e.key !== "]") return;

      // Only process if this tab is active in the focused panel
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      e.preventDefault();
      e.stopPropagation();

      const editorState = state.editorStates.get(tabId);
      const selectedIds = editorState?.selection?.selectedIds ?? [];
      const collapsed = editorState?.collapsedGroupIds ?? new Set<number>();

      for (const idStr of selectedIds) {
        const groupId = parseInt(idStr, 10);
        if (isNaN(groupId)) continue;
        if (e.key === "[" && !collapsed.has(groupId)) {
          // Ctrl+Shift+[ : collapse only expanded groups
          toggleCollapse(groupId);
        } else if (e.key === "]" && collapsed.has(groupId)) {
          // Ctrl+Shift+] : expand only collapsed groups
          toggleCollapse(groupId);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editorType, panelId, tabId, toggleCollapse]);

  // Clear hovered group on context menu open or scroll (Phase 26, Plan 02)
  useEffect(() => {
    if (contextMenu?.visible) {
      useEditorStore.getState().setHoveredGroup(tabId, null);
    }
  }, [contextMenu?.visible, tabId]);

  // Cleanup hover throttle timer on unmount
  useEffect(() => {
    return () => {
      if (hoverThrottleRef.current) {
        clearTimeout(hoverThrottleRef.current);
      }
    };
  }, []);

  // Listen for palette click-to-add (dispatches flowy:addElement with position="center").
  // Only the active tab IN THE FOCUSED PANEL processes the event, preventing
  // duplication across split panes.
  // Coordinates are scaled by devicePixelRatio to match the physical canvas pixel
  // dimensions (consistent with InputEvent.ts serializeMouseEvent).
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      // Only process if this panel is the focused panel
      if (state.focusedPanel !== panelId) return;
      // Only process if this tab is the active tab for this panel
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail;
      if (!detail?.type) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dpr = window.devicePixelRatio || 1;
      // "center" means place at viewport center; otherwise use provided position
      // Scale by DPR to match physical canvas pixel space (C++ Viewport operates in physical pixels)
      const x =
        (detail.position === "center" ? rect.width / 2 : (detail.position?.x ?? rect.width / 2)) *
        dpr;
      const y =
        (detail.position === "center" ? rect.height / 2 : (detail.position?.y ?? rect.height / 2)) *
        dpr;
      addElement(detail.type, x, y, detail.label ?? "");
    };
    window.addEventListener("flowy:addElement", handler);
    return () => window.removeEventListener("flowy:addElement", handler);
  }, [addElement, panelId, tabId]);

  // Listen for zoom commands from toolbar (flowy:zoomCommand).
  // detail.delta = relative zoom change; detail.fit = fit-to-view; detail.reset = zoom to 100%.
  // Only the active tab in the focused panel processes zoom commands.
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      if (detail.fit) {
        fitToView();
      } else if (detail.reset) {
        zoom(1.0);
      } else if (typeof detail.delta === "number") {
        // Compute new absolute zoom level from current viewport zoom + delta
        const editorState = state.editorStates.get(tabId);
        const currentZoom = editorState?.viewport.zoom ?? 1.0;
        const newLevel = Math.max(0.1, Math.min(3.0, currentZoom + detail.delta));
        zoom(newLevel);
      }
    };
    window.addEventListener("flowy:zoomCommand", handler);
    return () => window.removeEventListener("flowy:zoomCommand", handler);
  }, [zoom, fitToView, panelId, tabId]);

  // Listen for edit commands from toolbar (flowy:editCommand).
  // detail.action = "undo" | "redo" | "delete" | "copy" | "cut" | "paste" | "selectAll".
  // Only the active tab in the focused panel processes edit commands.
  useEffect(() => {
    const editActions: Record<string, () => void> = {
      undo,
      redo,
      delete: deleteSelected,
      copy,
      cut,
      paste,
      selectAll,
    };
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail;
      const action = detail?.action as string;
      if (action && editActions[action]) {
        editActions[action]();
      }
    };
    window.addEventListener("flowy:editCommand", handler);
    return () => window.removeEventListener("flowy:editCommand", handler);
  }, [undo, redo, deleteSelected, copy, cut, paste, selectAll, panelId, tabId]);

  // Listen for property changes from the Properties panel (flowy:propertyChange).
  // Forwards element property updates to the WASM editor worker for C++ model persistence.
  // Only the active tab in the focused panel processes property changes.
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail;
      if (!detail?.elementId || !detail?.properties) return;

      // Determine if this property change targets our editor type
      const targetEditorType = detail.editorType as string;
      const ourEditorType = editorType === "bpmn" ? "bpmn" : "nodeflow";
      if (targetEditorType !== ourEditorType) return;

      setProperty(detail.elementId as string, detail.properties as Record<string, unknown>);
    };
    window.addEventListener("flowy:propertyChange", handler);
    return () => window.removeEventListener("flowy:propertyChange", handler);
  }, [setProperty, editorType, panelId, tabId]);

  // Listen for auto-layout command (flowy:autoLayout).
  // detail.selectedOnly (boolean), detail.algorithm (number: 0=Sugiyama/DAG, 1=force-directed)
  // Only the active tab in the focused panel processes auto-layout.
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail ?? {};
      autoLayout(!!detail.selectedOnly, (detail.algorithm as number) ?? 0);
    };
    window.addEventListener("flowy:autoLayout", handler);
    return () => window.removeEventListener("flowy:autoLayout", handler);
  }, [autoLayout, panelId, tabId]);

  // Listen for save document command (flowy:saveDocument).
  // Triggers C++ export via worker, which posts back a documentSaved event.
  // The useEditorWorker hook handles the download via the documentSaved subscription.
  useEffect(() => {
    const handler = () => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      saveDocument();
    };
    window.addEventListener("flowy:saveDocument", handler);
    return () => window.removeEventListener("flowy:saveDocument", handler);
  }, [saveDocument, panelId, tabId]);

  // Listen for load document command (flowy:loadDocument).
  // detail: { content: string, format: "bpmn" | "nodeflow", tabId?: string }
  // Supports both targeted dispatch (detail.tabId matches this canvas) and
  // broadcast dispatch (no detail.tabId, active tab in focused panel handles it).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.content) return;

      // Targeted dispatch: if the event specifies a tabId, only that canvas handles it
      const targetTabId = detail.tabId as string | undefined;
      if (targetTabId) {
        if (targetTabId !== tabId) return;
      } else {
        // Broadcast dispatch: only the active tab in the focused panel handles it
        const state = useEditorStore.getState();
        if (state.focusedPanel !== panelId) return;
        if (state.panelActiveTabId[panelId] !== tabId) return;
      }

      // Only load if the format matches this editor type
      const format = detail.format as string;
      const isBpmn = editorType === "bpmn";
      if ((isBpmn && format === "bpmn") || (!isBpmn && format === "nodeflow")) {
        loadDocument(detail.content as string, format as "bpmn" | "nodeflow");
      }
    };
    window.addEventListener("flowy:loadDocument", handler);
    return () => window.removeEventListener("flowy:loadDocument", handler);
  }, [loadDocument, editorType, panelId, tabId]);

  // Auto-load initial content when the WASM worker becomes ready.
  // When a new tab is created with content (e.g. from AI generation or file import),
  // the EditorCanvas mounts and the worker initializes. Once ready, this effect
  // reads the tab's content field and loads it into the WASM editor automatically.
  useEffect(() => {
    if (!isReady) return;

    const state = useEditorStore.getState();
    const tab = state.tabs.find((t) => t.id === tabId);
    if (!tab?.content) return;

    // Only auto-load non-empty content that looks like actual diagram data
    const content = tab.content.trim();
    if (content.length === 0) return;

    const format = editorType === "bpmn" ? "bpmn" : "nodeflow";
    loadDocument(content, format);
  }, [isReady, tabId, editorType, loadDocument]);

  // Listen for N8N import command (flowy:importN8N).
  // Only the node editor handles this.
  useEffect(() => {
    if (editorType === "bpmn") return; // N8N import only for node editor
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail;
      if (!detail?.content) return;
      importN8N(detail.content as string);
    };
    window.addEventListener("flowy:importN8N", handler);
    return () => window.removeEventListener("flowy:importN8N", handler);
  }, [importN8N, editorType, panelId, tabId]);

  // Listen for tool mode changes from editorStore (flowy:setToolMode).
  // Forwards the tool mode integer to the WASM worker via postMessage.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.tabId || detail.tabId !== tabId) return;
      if (!workerId) return;
      const worker = getWorkerManager().getHost().getWorker(workerId);
      if (!worker) return;
      worker.postMessage({ type: "setToolMode", mode: detail.modeInt as number });
    };
    window.addEventListener("flowy:setToolMode", handler);
    return () => window.removeEventListener("flowy:setToolMode", handler);
  }, [tabId, workerId]);

  // Listen for cancel connect drag (flowy:cancelConnectDrag).
  // Dispatched when Escape is pressed in connection mode.
  useEffect(() => {
    const handler = () => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      if (!workerId) return;
      const worker = getWorkerManager().getHost().getWorker(workerId);
      if (!worker) return;
      worker.postMessage({ type: "cancelConnectDrag" });
    };
    window.addEventListener("flowy:cancelConnectDrag", handler);
    return () => window.removeEventListener("flowy:cancelConnectDrag", handler);
  }, [panelId, tabId, workerId]);

  // Listen for connection commands (flowy:addConnection).
  // detail: { sourceId, targetId, sourceSocket?, targetSocket? }
  // Only the active tab in the focused panel processes connection commands.
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail ?? {};
      if (typeof detail.sourceId === "number" && typeof detail.targetId === "number") {
        addConnection(detail.sourceId, detail.targetId, detail.sourceSocket, detail.targetSocket);
      }
    };
    window.addEventListener("flowy:addConnection", handler);
    return () => window.removeEventListener("flowy:addConnection", handler);
  }, [addConnection, panelId, tabId]);

  // Listen for create-and-connect commands from palette (flowy:createAndConnect).
  // Creates an element at the drop position and connects it from the pending source.
  useEffect(() => {
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;

      const detail = (e as CustomEvent).detail ?? {};
      if (!detail.elementType || typeof detail.sourceId !== "number") return;
      createAndConnect(
        detail.elementType as string,
        detail.x as number,
        detail.y as number,
        (detail.label as string) ?? "",
        detail.sourceId as number,
        (detail.sourceSocket as string) ?? "",
      );
    };
    window.addEventListener("flowy:createAndConnect", handler);
    return () => window.removeEventListener("flowy:createAndConnect", handler);
  }, [createAndConnect, panelId, tabId]);

  // Double-click listener on canvas: start inline text editing for selected BPMN element.
  // Only starts editing when exactly one element is selected.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || editorType !== "bpmn") return;
    const handler = () => {
      const state = useEditorStore.getState();
      const editorState = state.editorStates.get(tabId);
      const selectedIds = editorState?.selection.selectedIds ?? [];
      if (selectedIds.length === 1) {
        startInlineEdit(parseInt(selectedIds[0], 10));
      }
    };
    el.addEventListener("dblclick", handler);
    return () => el.removeEventListener("dblclick", handler);
  }, [startInlineEdit, editorType, tabId]);

  // Subscribe to inlineEditStarted event from WorkerManager to show the overlay.
  useEffect(() => {
    const manager = getWorkerManager();
    const unsub = manager.onEditorEvent("inlineEditStarted", (sourceId, env) => {
      if (sourceId !== workerId) return;
      const payload = env.payload as
        | { active: boolean; text: string; x: number; y: number; width: number; height: number }
        | undefined;
      if (!payload?.active) {
        setInlineEdit(null);
        return;
      }
      // Convert world-space coordinates to screen-space using viewport info
      const editorState = useEditorStore.getState().editorStates.get(tabId);
      const viewportZoom = editorState?.viewport.zoom ?? 1.0;
      const viewportCenterX = editorState?.viewport.centerX ?? 500;
      const viewportCenterY = editorState?.viewport.centerY ?? 400;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      // World-to-screen: screen = (world - center) * zoom + canvas_center
      const screenX = (payload.x - viewportCenterX) * viewportZoom + rect.width / 2;
      const screenY = (payload.y - viewportCenterY) * viewportZoom + rect.height / 2;
      setInlineEdit({
        active: true,
        text: payload.text,
        x: screenX,
        y: screenY,
        width: payload.width * viewportZoom,
        height: payload.height * viewportZoom,
      });
    });
    return unsub;
  }, [workerId, tabId]);

  // Listen for flowy:validate CustomEvent -- triggers validation on the BPMN editor.
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = () => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      validate();
    };
    window.addEventListener("flowy:validate", handler);
    return () => window.removeEventListener("flowy:validate", handler);
  }, [validate, editorType, panelId, tabId]);

  // Listen for flowy:setFlowCondition CustomEvent from PropertyForm.
  // detail: { connectionId: number, expression: string }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.connectionId === "number") {
        setFlowCondition(detail.connectionId, detail.expression ?? "");
      }
    };
    window.addEventListener("flowy:setFlowCondition", handler);
    return () => window.removeEventListener("flowy:setFlowCondition", handler);
  }, [setFlowCondition, editorType, panelId, tabId]);

  // Listen for flowy:setDefaultFlow CustomEvent from PropertyForm.
  // detail: { connectionId: number, isDefault: boolean }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.connectionId === "number") {
        setDefaultFlow(detail.connectionId, !!detail.isDefault);
      }
    };
    window.addEventListener("flowy:setDefaultFlow", handler);
    return () => window.removeEventListener("flowy:setDefaultFlow", handler);
  }, [setDefaultFlow, editorType, panelId, tabId]);

  // Listen for flowy:setMultiInstance CustomEvent from PropertyForm.
  // detail: { groupId: number, multiInstanceType: number }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.groupId === "number") {
        setMultiInstance(detail.groupId, detail.multiInstanceType ?? 0);
      }
    };
    window.addEventListener("flowy:setMultiInstance", handler);
    return () => window.removeEventListener("flowy:setMultiInstance", handler);
  }, [setMultiInstance, editorType, panelId, tabId]);

  // Listen for flowy:attachBoundaryEvent CustomEvent.
  // detail: { eventId: number, parentId: number, position?: number }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.eventId === "number" && typeof detail?.parentId === "number") {
        attachBoundaryEvent(detail.eventId, detail.parentId, detail.position);
      }
    };
    window.addEventListener("flowy:attachBoundaryEvent", handler);
    return () => window.removeEventListener("flowy:attachBoundaryEvent", handler);
  }, [attachBoundaryEvent, editorType, panelId, tabId]);

  // Listen for flowy:detachBoundaryEvent CustomEvent.
  // detail: { eventId: number }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.eventId === "number") {
        detachBoundaryEvent(detail.eventId);
      }
    };
    window.addEventListener("flowy:detachBoundaryEvent", handler);
    return () => window.removeEventListener("flowy:detachBoundaryEvent", handler);
  }, [detachBoundaryEvent, editorType, panelId, tabId]);

  // Listen for flowy:toggleSubprocess CustomEvent.
  // detail: { subprocessId: number }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.subprocessId === "number") {
        toggleSubprocess(detail.subprocessId);
      }
    };
    window.addEventListener("flowy:toggleSubprocess", handler);
    return () => window.removeEventListener("flowy:toggleSubprocess", handler);
  }, [toggleSubprocess, editorType, panelId, tabId]);

  // Listen for flowy:createPool CustomEvent.
  // detail: { x: number, y: number, name?: string }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.x === "number" && typeof detail?.y === "number") {
        createPool(detail.x, detail.y, detail.name);
      }
    };
    window.addEventListener("flowy:createPool", handler);
    return () => window.removeEventListener("flowy:createPool", handler);
  }, [createPool, editorType, panelId, tabId]);

  // Listen for flowy:createLane CustomEvent.
  // detail: { poolId: number, name?: string }
  useEffect(() => {
    if (editorType !== "bpmn") return;
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.poolId === "number") {
        createLane(detail.poolId, detail.name);
      }
    };
    window.addEventListener("flowy:createLane", handler);
    return () => window.removeEventListener("flowy:createLane", handler);
  }, [createLane, editorType, panelId, tabId]);

  // Listen for flowy:addSocket CustomEvent from NodePropertyForm.
  // detail: { groupId: number, socketId: string, name: string, dataType: string, isInput: boolean }
  useEffect(() => {
    if (editorType === "bpmn") return; // Node editor only
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.groupId === "number") {
        addSocket(
          detail.groupId,
          detail.socketId ?? "",
          detail.name ?? "",
          detail.dataType ?? "any",
          !!detail.isInput,
        );
      }
    };
    window.addEventListener("flowy:addSocket", handler);
    return () => window.removeEventListener("flowy:addSocket", handler);
  }, [addSocket, editorType, panelId, tabId]);

  // Listen for flowy:removeSocket CustomEvent from NodePropertyForm.
  // detail: { groupId: number, socketId: string, isInput: boolean }
  useEffect(() => {
    if (editorType === "bpmn") return; // Node editor only
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.groupId === "number") {
        removeSocket(detail.groupId, detail.socketId ?? "", !!detail.isInput);
      }
    };
    window.addEventListener("flowy:removeSocket", handler);
    return () => window.removeEventListener("flowy:removeSocket", handler);
  }, [removeSocket, editorType, panelId, tabId]);

  // Listen for flowy:groupSelected CustomEvent.
  // detail: { label?: string }
  useEffect(() => {
    if (editorType === "bpmn") return; // Node editor only
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail ?? {};
      groupSelected(detail.label);
    };
    window.addEventListener("flowy:groupSelected", handler);
    return () => window.removeEventListener("flowy:groupSelected", handler);
  }, [groupSelected, editorType, panelId, tabId]);

  // Listen for flowy:dissolveGroup CustomEvent.
  // detail: { groupId: number }
  useEffect(() => {
    if (editorType === "bpmn") return; // Node editor only
    const handler = (e: Event) => {
      const state = useEditorStore.getState();
      if (state.focusedPanel !== panelId) return;
      if (state.panelActiveTabId[panelId] !== tabId) return;
      const detail = (e as CustomEvent).detail;
      if (typeof detail?.groupId === "number") {
        dissolveGroup(detail.groupId);
      }
    };
    window.addEventListener("flowy:dissolveGroup", handler);
    return () => window.removeEventListener("flowy:dissolveGroup", handler);
  }, [dissolveGroup, editorType, panelId, tabId]);

  // Inline edit: commit on Enter or blur, cancel on Escape.
  const inlineEditRef = useRef<HTMLInputElement>(null);
  const handleInlineEditKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commitInlineEdit(e.currentTarget.value);
        setInlineEdit(null);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelInlineEdit();
        setInlineEdit(null);
      }
    },
    [commitInlineEdit, cancelInlineEdit],
  );
  const handleInlineEditBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      commitInlineEdit(e.currentTarget.value);
      setInlineEdit(null);
    },
    [commitInlineEdit],
  );
  // Auto-focus inline edit input when it appears
  useEffect(() => {
    if (inlineEdit?.active && inlineEditRef.current) {
      inlineEditRef.current.focus();
      inlineEditRef.current.select();
    }
  }, [inlineEdit?.active]);

  // Drop handler for palette drag-to-canvas.
  // Coordinates are scaled by devicePixelRatio to match the physical canvas pixel
  // dimensions (consistent with InputEvent.ts serializeMouseEvent).
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const mimeType =
        editorType === "bpmn" ? "application/flowy-bpmn-element" : "application/flowy-node-element";
      const data = e.dataTransfer.getData(mimeType);
      if (!data) return;

      try {
        const item = JSON.parse(data) as { type: string; label: string };
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const dpr = window.devicePixelRatio || 1;
        const x = (e.clientX - rect.left) * dpr;
        const y = (e.clientY - rect.top) * dpr;
        addElement(item.type, x, y, item.label);
      } catch {
        // Invalid drag data -- ignore
      }
    },
    [addElement, editorType],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const expectedType =
        editorType === "bpmn" ? "application/flowy-bpmn-element" : "application/flowy-node-element";
      if (!e.dataTransfer.types.includes(expectedType)) {
        e.dataTransfer.dropEffect = "none";
        e.preventDefault();
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    [editorType],
  );

  return (
    <div
      ref={containerRef}
      className={`relative flex-1 overflow-hidden bg-zinc-950 ${cursorClass}`}
      data-tab-id={tabId}
      data-worker-id={workerId}
      data-panel-id={panelId}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onContextMenu={handleContextMenu}
        tabIndex={0}
      />

      {/* Inline text edit overlay (Phase 15, Plan 09 -- gap closure) */}
      {inlineEdit?.active && (
        <input
          ref={inlineEditRef}
          type="text"
          defaultValue={inlineEdit.text}
          onKeyDown={handleInlineEditKeyDown}
          onBlur={handleInlineEditBlur}
          className="absolute z-50 border border-blue-500 bg-zinc-900 px-1 text-sm text-white outline-none"
          style={{
            left: `${inlineEdit.x}px`,
            top: `${inlineEdit.y}px`,
            width: `${Math.max(inlineEdit.width, 80)}px`,
            height: `${Math.max(inlineEdit.height, 24)}px`,
            textAlign: "center",
          }}
        />
      )}

      {/* Loading overlay */}
      {!isReady && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/80">
          <div className="flex flex-col items-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-blue-500" />
            <span className="text-xs text-zinc-400">
              Loading {editorType === "bpmn" ? "BPMN" : "Node"} editor...
            </span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-950/80">
          <div className="flex flex-col items-center gap-2 max-w-sm text-center">
            <span className="text-sm text-red-400">Editor Error</span>
            <span className="text-xs text-zinc-400">{error}</span>
          </div>
        </div>
      )}

      {/* Context menu overlay for node editor collapse/expand (Phase 26, Plan 02) */}
      {contextMenu?.visible && (
        <div
          className="fixed z-50 min-w-36 rounded border border-zinc-700 bg-zinc-800 py-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            type="button"
            className="w-full px-3 py-1.5 text-left text-xs text-zinc-200 hover:bg-zinc-700"
            onClick={handleCollapseMenuClick}
          >
            {(() => {
              const state = useEditorStore.getState();
              const editorState = state.editorStates.get(tabId);
              const selectedIds = editorState?.selection?.selectedIds ?? [];
              if (selectedIds.length > 0) {
                const groupId = parseInt(selectedIds[0], 10);
                if (collapsedGroupIds?.has(groupId)) return "Expand group";
              }
              return "Collapse group";
            })()}
          </button>
        </div>
      )}

      {/* Hover tooltip for collapsed groups (Phase 26, Plan 02) */}
      {hoveredGroup && hoveredGroup.collapsed && hoveredGroup.childNames.length > 0 && (
        <div
          className="absolute z-50 max-w-48 rounded border border-zinc-600 bg-zinc-800 px-2 py-1 text-xs shadow-lg pointer-events-none text-zinc-200"
          style={{
            left: hoveredGroup.screenX / (window.devicePixelRatio || 1) + 12,
            top: hoveredGroup.screenY / (window.devicePixelRatio || 1) + 12,
          }}
        >
          <div className="mb-0.5 font-medium">Group nodes:</div>
          {hoveredGroup.childNames.map((name, i) => (
            <div key={i} className="truncate text-zinc-400">
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
